import { useEffect, useRef, useState } from "react";
import { hasCapability, useAdmin } from "components/admin/RequireAdmin";
import { ErrorNotice, Loading, useRemote } from "components/admin/workspace/Controls";
import PaperworkPrices from "components/admin/settings/PaperworkPrices";
import { useConfirm } from "components/ios/Alert";
import { Segmented } from "components/ios/Effects";
import { adminRequest, operationKey } from "lib/adminSiteApi";
import { announcePaperworkContentChanged } from "lib/paperworkContent";
import { EQUIPMENT, STATE_EDITIONS } from "data/paperworkVariants";
import { ARRANGEMENTS } from "components/paperwork/content";
import Editions from "./Editions";

const TIERS = [["basic", "Basic"], ["standard", "Standard"], ["premium", "Premium"]];
const CHAPTERS = [["overview", "Overview"], ["policies", "Policies"], ["procedures", "Procedures"], ["forms", "Forms"], ["workbooks", "Workbooks"]];
const CHAPTER_FIELDS = [
  ["problem", "The problem"],
  ["consequence", "Possible consequences"],
  ["workingArrangement", "Your working arrangement"],
  ["packageCovers", "What the package covers"],
  ["scope", "Included scope"],
  ["offerNote", "Written offer note"],
];
const copy = value => JSON.parse(JSON.stringify(value));

function ContentForm({ path, data, selection, refreshing, onReload, onDirtyChange, onBusyChange }) {
  const [draft, setDraft] = useState(() => copy(data.value));
  const [busy, setBusy] = useState(false), [error, setError] = useState(null), [notice, setNotice] = useState("");
  const [confirmation, ask] = useConfirm();
  const baseline = useRef(data);
  const pending = useRef(null);
  const alive = useRef(true);
  const specific = selection.scope === "selection";
  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline.current.value);
  useEffect(() => {
    baseline.current = data;
    setDraft(copy(data.value));
    pending.current = null;
    setError(null);
  }, [data]);
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { onBusyChange(busy); }, [busy, onBusyChange]);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; onDirtyChange(false); onBusyChange(false); };
  }, [onDirtyChange, onBusyChange]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async (event, inherit = false) => {
    event?.preventDefault();
    if (busy || refreshing) return;
    if (inherit && !(await ask({ title: "Use package defaults?", message: "This removes the saved wording for this exact selection. Visitors will see this package’s current defaults.", confirm: "Use package defaults" }))) return;
    const body = { expectedRevision: baseline.current.revision,
      ...(specific ? { expectedDefaultRevision: baseline.current.defaultRevision } : {}),
      ...(inherit ? { inherit: true } : { value: draft }) };
    const digest = JSON.stringify(body);
    if (pending.current?.digest !== digest) pending.current = { digest, key: operationKey() };
    setBusy(true); setError(null); setNotice("");
    try {
      const result = await adminRequest(path, { method: "PUT", key: pending.current.key, body });
      if (!alive.current) return;
      pending.current = null;
      if (result.data?.value) {
        baseline.current = result.data;
        setDraft(copy(result.data.value));
      } else baseline.current = { ...baseline.current, value: copy(draft) };
      onDirtyChange(false);
      setNotice(inherit ? "Saved. This selection now uses package defaults." : "Saved. The Paperwork page uses this wording now.");
      announcePaperworkContentChanged();
      onReload();
    } catch (err) { if (alive.current) setError(err); }
    finally { if (alive.current) setBusy(false); }
  };
  const reload = async () => {
    if (dirty && !(await ask({ title: "Discard unsaved wording?", message: "Reloading replaces your unsaved edits with the latest saved content.", confirm: "Discard and reload" }))) return;
    setNotice(""); onReload();
  };
  const setChapter = (chapter, field, value) => setDraft(current => ({ ...current, chapters: {
    ...current.chapters, [chapter]: { ...current.chapters[chapter], [field]: value },
  } }));
  const publicSearch = new URLSearchParams({ package: selection.tier, ...(specific ? {
    arrangement: selection.arrangement, equipment: selection.equipment, state: selection.state,
  } : {}) });
  return <section className="aw-panel" aria-labelledby="paperwork-wording-title">
    {confirmation}
    <h2 id="paperwork-wording-title">{TIERS.find(([id]) => id === selection.tier)[1]} · {specific ? "Selected-option content" : "Package defaults"}</h2>
    <p className="aw-muted">{specific
      ? data.inherited ? "This selection uses package defaults. Saving creates wording for only this working arrangement, equipment and state."
        : "This wording is saved for only this working arrangement, equipment and state. Other selections keep their own wording."
      : "The title and package details appear on the Paperwork page and in the chooser. These chapters apply wherever this package has no selection-specific wording."}</p>
    <form onSubmit={save}>
      <fieldset disabled={busy || refreshing}>
        {!specific && <div className="aw-fields">
          <label className="aw-wide">Package title<input required maxLength={300} value={draft.title} onChange={event => setDraft({ ...draft, title: event.target.value })} /></label>
          <label className="aw-wide">Package details<textarea required rows={5} maxLength={6000} value={draft.description} onChange={event => setDraft({ ...draft, description: event.target.value })} /></label>
        </div>}
        <h3>Package chapters</h3>
        <p className="aw-muted">Plain text only. Leave “Your working arrangement” empty to use the selected arrangement’s explanation. Leave “What the package covers” empty to use the package details.</p>
        {CHAPTERS.map(([chapter, label]) => <details className="aw-array" key={chapter}>
          <summary>{label}</summary>
          <fieldset className="aw-array"><legend>{label} content</legend><div className="aw-fields">
            {CHAPTER_FIELDS.map(([field, title]) => <label className="aw-wide" key={field}>{title}<textarea rows={3} maxLength={6000} required={["problem", "consequence", "scope"].includes(field)}
              value={draft.chapters[chapter][field]} onChange={event => setChapter(chapter, field, event.target.value)} /></label>)}
          </div></fieldset>
        </details>)}
        <ErrorNotice error={error} />
        {notice && <p className="aw-notice" role="status">{notice}</p>}
        {refreshing && <p className="aw-notice" role="status">Reading saved wording…</p>}
        <div className="aw-row">
          <button type="submit" className="aw-button aw-primary" disabled={!dirty}>{busy ? "Saving…" : "Save public content"}</button>
          <button type="button" className="aw-button" onClick={reload}>Reload latest</button>
          {specific && !data.inherited && <button type="button" className="aw-button" onClick={event => save(event, true)}>Use package defaults</button>}
          <a className="aw-button" href={`/paperwork?${publicSearch}`} target="_blank" rel="noopener noreferrer">Open public selection</a>
        </div>
        <p className="aw-muted">Saved version {data.revision}{specific ? ` · Package defaults version ${data.defaultRevision}` : ""}. {dirty ? "Unsaved changes." : "No unsaved changes."}</p>
      </fieldset>
    </form>
  </section>;
}

function ContentRecord({ selection, onDirtyChange, onBusyChange }) {
  const [tick, setTick] = useState(0);
  const path = `/admin/paperwork/content/${selection.tier}` + (selection.scope === "selection"
    ? `/${selection.arrangement}/${selection.equipment}/${selection.state}` : "");
  const state = useRemote(path, tick, { keep: true });
  return <Loading state={state}>{state.data && <ContentForm key={path} path={path} data={state.data} selection={selection}
    refreshing={state.loading} onReload={() => setTick(value => value + 1)} onDirtyChange={onDirtyChange} onBusyChange={onBusyChange} />}</Loading>;
}

export default function PaperworkManagement({ id }) {
  const profile = useAdmin();
  const [tab, setTab] = useState("content"), [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selection, setSelection] = useState({ tier: "basic", scope: "defaults", arrangement: "w2", equipment: "box-truck", state: "AL" });
  const [confirmation, ask] = useConfirm();
  const navigation = useRef(false);
  const change = async action => {
    if (navigation.current || busy) return;
    navigation.current = true;
    try {
      if (dirty && !(await ask({ title: "Discard unsaved wording?", message: "Save your content first, or discard these edits to change the selection or section.", confirm: "Discard changes" }))) return;
      setDirty(false); action();
    } finally { navigation.current = false; }
  };
  if (id) return <Editions id={id} />;
  const tabs = [{ value: "content", label: "Package content" }, ...(hasCapability(profile, "commerce.prices.manage") ? [{ value: "prices", label: "Prices & sales" }] : []), { value: "editions", label: "Delivery editions" }];
  const choose = (field, value) => { if (selection[field] !== value) change(() => setSelection(current => ({ ...current, [field]: value }))); };
  return <div className="aw-paperwork-management">
    {confirmation}
    <Segmented label="Paperwork management" className="aw-tabs" items={tabs} value={tab} busy={busy} onChange={value => { if (value !== tab) change(() => setTab(value)); }} />
    {tab === "prices" ? <PaperworkPrices /> : tab === "editions" ? <Editions /> : <>
      <section className="aw-panel" aria-labelledby="paperwork-selection-title">
        <h2 id="paperwork-selection-title">Paperwork content</h2>
        <p className="aw-muted">Choose the package and which public wording to update. Prices and sale switches are in Prices &amp; sales; paid delivery files remain in Delivery editions.</p>
        <fieldset disabled={busy}><div className="aw-row">
          <label>Package<select value={selection.tier} onChange={event => choose("tier", event.target.value)}>{TIERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Content scope<select value={selection.scope} onChange={event => choose("scope", event.target.value)}><option value="defaults">Package defaults</option><option value="selection">Specific selection</option></select></label>
        </div>
        {selection.scope === "selection" && <div className="aw-row">
          <label>Working arrangement<select value={selection.arrangement} onChange={event => choose("arrangement", event.target.value)}>{ARRANGEMENTS.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
          <label>Equipment<select value={selection.equipment} onChange={event => choose("equipment", event.target.value)}>{EQUIPMENT.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
          <label>State edition<select value={selection.state} onChange={event => choose("state", event.target.value)}>{STATE_EDITIONS.map(item => <option key={item.code} value={item.code}>{item.name}</option>)}</select></label>
        </div>}</fieldset>
      </section>
      <ContentRecord selection={selection} onDirtyChange={setDirty} onBusyChange={setBusy} />
    </>}
  </div>;
}
